export interface MatchRecord {
  id: string;
  timestamp: number;
  mode: 'cpu' | 'online';
  opponentName: string;
  myScore: number;
  opponentScore: number;
  won: boolean;
}

export interface UserProfile {
  uid: string;
  displayName: string;
  xp: number;
  level: number;
  stats: {
    played: number;
    won: number;
    lost: number;
  };
  avatar?: string;
  title?: string;
  history?: MatchRecord[];
  isPremium?: boolean;
  createdAt?: number;
  coins?: CoinsData;
  achievements?: AchievementsData;
  equipped?: EquippedSkins;
  owned?: OwnedSkins;
  streak?: number;
  dailyChallenges?: { [date: string]: { completed: string[] } };
}

/**
 * Calculates user level based on XP.
 * Level 1: 0 - 99 XP
 * Level 2: 100 - 199 XP
 * Level 3: 200 - 299 XP
 * etc.
 * Formula: Level = floor(XP / 100) + 1
 */
export function getLevelForXP(xp: number): number {
  if (xp < 0) return 1;
  return Math.floor(xp / 100) + 1;
}

/**
 * Calculates minimum XP required to reach a specific level.
 * Formula: XP = Level * 100
 */
export function getXPForLevel(level: number): number {
  if (level <= 1) return 0;
  return (level - 1) * 100;
}

export interface LevelProgress {
  level: number;
  currentLevelXpStart: number;
  nextLevelXpStart: number;
  xpInCurrentLevel: number;
  xpForNextLevel: number;
  progressPercent: number;
}

/**
 * Returns detailed level and progression info for UI rendering.
 */
export function getLevelProgress(xp: number): LevelProgress {
  const level = getLevelForXP(xp);
  const currentLevelXpStart = getXPForLevel(level);
  const nextLevelXpStart = getXPForLevel(level + 1);
  
  const xpInCurrentLevel = Math.max(0, xp - currentLevelXpStart);
  const xpForNextLevel = nextLevelXpStart - currentLevelXpStart;
  const progressPercent = xpForNextLevel > 0 
    ? Math.min(100, Math.max(0, (xpInCurrentLevel / xpForNextLevel) * 100))
    : 0;

  return {
    level,
    currentLevelXpStart,
    nextLevelXpStart,
    xpInCurrentLevel,
    xpForNextLevel,
    progressPercent,
  };
}

// === COIN SYSTEM ===

export interface CoinsData {
  balance: number;
  lifetimeEarned: number;
  lifetimeSpent: number;
}

export interface AchievementsData {
  [id: string]: number;
}

export interface EquippedSkins {
  cardBack: string;
  boardTheme: string;
  cardAnim: string | null;
  avatarFrame: string | null;
}

export interface OwnedSkins {
  cardBacks: string[];
  boardThemes: string[];
  cardAnims: string[];
  extras: string[];
}

export const DEFAULT_COINS: CoinsData = {
  balance: 0,
  lifetimeEarned: 0,
  lifetimeSpent: 0,
};

export const DEFAULT_EQUIPPED: EquippedSkins = {
  cardBack: 'cb_classic',
  boardTheme: 'bt_green',
  cardAnim: null,
  avatarFrame: null,
};

export const DEFAULT_OWNED: OwnedSkins = {
  cardBacks: ['cb_classic'],
  boardThemes: ['bt_green'],
  cardAnims: [],
  extras: [],
};
